import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../../../components/ui/dialog';
import { Button } from '../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../../../components/ui/table';
import { cn } from '../../../../../../../lib/utils';
import { DeleteConfirmationDialog } from '../../../../../../../components/ui/DeleteConfirmationDialog';
import {
  COMMON_UNIT_OPTIONS,
  FREQUENCY_OPTIONS,
  FREQUENCY_RULES,
  LIMIT_OPERATOR_OPTIONS,
  PROJECT_LIMIT_FIELDS,
  PROJECT_LIMITS,
  REPORT_TABS,
  type FrequencyRule,
  type LimitOperator,
  type MockProjectLimit,
  type ProjectLimitFieldOption,
  type ReportFilter,
  type ReportType,
} from '../config/projectLimitsConfig';
import { usePipeMillReport } from '../../../../../../../redux/Project/pipeMillReport/hooks/usePipeMillReport';
import { pipeMillReportApi } from '../../../../../../../redux/Project/pipeMillReport/api';

interface ProjectLimitsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  selectedReportType: string;
  metadataByType: Record<string, unknown>;
  selectedPipe: any;
  details: any;
  selectedMaterial: any;
}

const inputClassName =
  'h-10 w-full rounded-lg border border-[#E2E5EA] bg-white px-3 text-[13px] text-text-grey outline-none focus:ring-2 focus:ring-hero-red-10';

type LocalFrequencyRule = FrequencyRule & {
  projectLimitId?: number | string;
  project_limit_id?: number | string;
};

type ProjectLimitDeleteTarget =
  | { kind: 'limit'; item: MockProjectLimit }
  | { kind: 'frequency'; item: LocalFrequencyRule };

const isNumericId = (id?: string | number | null): boolean =>
  id != null && /^\d+$/.test(String(id));

const toReportType = (value: unknown, fallback: ReportType = 'HYDROSTATIC'): ReportType => {
  const report = String(value || '');
  return REPORT_TABS.some((tab) => tab.key === report && tab.key !== 'ALL') ? report as ReportType : fallback;
};

const pipeIdFrom = (selectedPipe: any): number | undefined => {
  const raw = selectedPipe?.pipeId ?? selectedPipe?.pipe_id ?? selectedPipe?.id;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const mapApiLimit = (limit: any): MockProjectLimit => ({
  id: String(limit.id ?? limit.limitId ?? `limit-${limit.reportType || limit.report}-${limit.fieldKey || limit.field}`),
  report: toReportType(limit.reportType ?? limit.report_type ?? limit.report),
  field: String(limit.fieldKey ?? limit.field_key ?? limit.field ?? 'measured_value'),
  operator: (limit.detailsJson?.operator ?? limit.details_json?.operator ?? undefined) as LimitOperator | undefined,
  minValue: String(limit.minValue ?? limit.min_value ?? ''),
  maxValue: String(limit.maxValue ?? limit.max_value ?? ''),
  unit: String(limit.unit ?? ''),
  source: String(limit.source ?? limit.remarks ?? ''),
  std: String(limit.std ?? limit.detailsJson?.standardFallback ?? limit.details_json?.standardFallback ?? ''),
});

const mapApiFrequencyRule = (rule: any): LocalFrequencyRule => ({
  id: String(rule.id ?? rule.ruleId ?? `freq-${rule.reportType || rule.report}-${rule.frequencyType || rule.frequency}`),
  projectLimitId: rule.projectLimitId ?? rule.project_limit_id,
  project_limit_id: rule.project_limit_id ?? rule.projectLimitId,
  report: toReportType(rule.projectLimit?.reportType ?? rule.projectLimit?.report_type ?? rule.reportType ?? rule.report_type ?? rule.report),
  frequency: String(rule.frequency ?? rule.frequencyType ?? rule.frequency_type ?? 'per_pipe'),
  lotSize: String(rule.lotSize ?? rule.sampleSize ?? rule.sample_size ?? '-'),
  pipeCount: String(rule.pipeCount ?? rule.frequencyValue ?? rule.frequency_value ?? '-'),
  source: String(rule.source ?? rule.detailsJson?.source ?? rule.details_json?.source ?? ''),
  reference: String(rule.reference ?? rule.remarks ?? ''),
});

const mapDefaultLimitPayload = (limit: MockProjectLimit) => ({
  reportType: limit.report,
  fieldKey: limit.field,
  minValue: limit.minValue,
  maxValue: limit.maxValue,
  unit: limit.unit,
  source: limit.source,
  std: limit.std,
  operator: getLimitOperator(limit),
});

const mapDefaultFrequencyPayload = (rule: FrequencyRule) => ({
  reportType: rule.report,
  frequency: rule.frequency,
  frequencyValue: rule.pipeCount,
  sampleSize: rule.lotSize,
  lotSize: rule.lotSize,
  pipeCount: rule.pipeCount,
  source: rule.source,
  reference: rule.reference,
});

const getDefaultFieldOption = (report: ReportType): ProjectLimitFieldOption =>
  PROJECT_LIMIT_FIELDS[report]?.[0] || { fieldPath: 'measured_value', label: 'Measured value', unit: '', fallback: 'Mock standard fallback' };

const formatProjectFieldOption = (field: ProjectLimitFieldOption): string =>
  field.symbol ? `${field.symbol} - ${field.label} (${field.fieldPath})` : `${field.label} (${field.fieldPath})`;

const createFieldPathFromLabel = (report: ReportType, label: string, symbol: string): string => {
  if (report === 'CHEMICAL' && symbol.trim()) return `elements.${symbol.trim()}`;
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'custom_field';
};

const createBlankLimit = (report: ReportType = 'HYDROSTATIC'): MockProjectLimit => ({
  id: `draft-${Date.now()}`,
  report,
  field: getDefaultFieldOption(report).fieldPath,
  operator: 'min',
  minValue: '',
  maxValue: '',
  unit: getDefaultFieldOption(report).unit,
  source: '',
  std: getDefaultFieldOption(report).fallback,
});

const createBlankFrequencyRule = (report: ReportType = 'HYDROSTATIC'): FrequencyRule => ({
  id: `freq-draft-${Date.now()}`,
  report,
  frequency: 'per_pipe',
  lotSize: '',
  pipeCount: '',
  source: '',
  reference: '',
});

const getLimitOperator = (limit: MockProjectLimit): LimitOperator => {
  if (limit.operator) return limit.operator;
  if (limit.minValue && limit.maxValue && limit.minValue === limit.maxValue) return 'equals';
  if (limit.minValue && limit.maxValue) return 'range';
  if (limit.minValue) return 'min';
  return 'max';
};

const getOperatorLabel = (operator: LimitOperator) =>
  LIMIT_OPERATOR_OPTIONS.find((option) => option.value === operator)?.tableLabel || operator;

const ensureConfiguredFieldLimits = (currentLimits: MockProjectLimit[]): MockProjectLimit[] => currentLimits;

export const ProjectLimitsModal: React.FC<ProjectLimitsModalProps> = ({
  isOpen,
  onOpenChange,
  projectId,
  selectedReportType,
  selectedPipe,
  details,
}) => {
  const {
    projectLimits,
    projectLimitsLoading,
    projectLimitsError,
    frequencyRules: apiFrequencyRules,
    frequencyRulesLoading,
    frequencyRulesError,
    fetchProjectLimits,
    saveProjectLimit,
    deleteProjectLimit: deleteProjectLimitApi,
    fetchFrequencyRules,
    saveFrequencyRule: saveFrequencyRuleApi,
    deleteFrequencyRule: deleteFrequencyRuleApi,
  } = usePipeMillReport();
  const [activeReport, setActiveReport] = useState<ReportFilter>('ALL');
  const [limits, setLimits] = useState<MockProjectLimit[]>([]);
  const [frequencyRules, setFrequencyRules] = useState<LocalFrequencyRule[]>([]);
  const [isResettingDefaults, setIsResettingDefaults] = useState(false);
  const [editing, setEditing] = useState<MockProjectLimit | null>(null);
  const [isFrequencyRuleModalOpen, setIsFrequencyRuleModalOpen] = useState(false);
  const [frequencyRuleForm, setFrequencyRuleForm] = useState<FrequencyRule>(() => createBlankFrequencyRule());
  const [editingFrequencyRuleId, setEditingFrequencyRuleId] = useState<string | null>(null);
  const [customFieldOptionsByType, setCustomFieldOptionsByType] = useState<Partial<Record<ReportType, ProjectLimitFieldOption[]>>>({});
  const [isFieldOptionModalOpen, setIsFieldOptionModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProjectLimitDeleteTarget | null>(null);
  const [fieldOptionForm, setFieldOptionForm] = useState<ProjectLimitFieldOption>({
    fieldPath: '',
    symbol: '',
    label: '',
    unit: '',
    fallback: '',
  });

  const itpNumber = selectedPipe?.itpNumber || details?.itpNumber || details?.pipePurchaseOrderNumber || 'ITP-NRI-2024-08-Rev-C';
  const pipeId = pipeIdFrom(selectedPipe);

  useEffect(() => {
    if (!isOpen || !projectId) return;
    setLimits([]);
    setFrequencyRules([]);
    const payload = {
      projectId,
      ...(pipeId ? { pipeId } : {}),
    };
    fetchProjectLimits(payload);
    fetchFrequencyRules(payload);
    // Redux hook action creators are not memoized; keep this effect scoped to modal context changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pipeId, projectId]);

  useEffect(() => {
    setLimits(Array.isArray(projectLimits) ? projectLimits.map(mapApiLimit) : []);
  }, [projectLimits]);

  useEffect(() => {
    setFrequencyRules(Array.isArray(apiFrequencyRules) ? apiFrequencyRules.map(mapApiFrequencyRule) : []);
  }, [apiFrequencyRules]);

  const displayLimits = useMemo(() => ensureConfiguredFieldLimits(limits), [limits]);
  const isProjectLimitsLoading = Boolean(projectLimitsLoading || isResettingDefaults);
  const isFrequencyRulesLoading = Boolean(frequencyRulesLoading || isResettingDefaults);
  const filteredLimits = useMemo(
    () => displayLimits.filter((limit) => activeReport === 'ALL' || limit.report === activeReport),
    [displayLimits, activeReport]
  );
  const getAvailableFieldOptions = (report: ReportType): ProjectLimitFieldOption[] => [
    ...(PROJECT_LIMIT_FIELDS[report] || [getDefaultFieldOption(report)]),
    ...(customFieldOptionsByType[report] || []),
  ];
  const getUnitOptions = (report: ReportType, currentUnit?: string): string[] => {
    const units = new Set<string>(COMMON_UNIT_OPTIONS);
    getAvailableFieldOptions(report).forEach((field) => units.add(field.unit));
    limits.filter((limit) => limit.report === report).forEach((limit) => units.add(limit.unit));
    if (currentUnit != null) units.add(currentUnit);
    return Array.from(units);
  };

  const openAddLimit = () => {
    const requestedReport = REPORT_TABS.some((tab) => tab.key === selectedReportType)
      ? selectedReportType as ReportType
      : 'HYDROSTATIC';
    const report = activeReport !== 'ALL' ? activeReport : requestedReport;
    setEditing(createBlankLimit(report));
    setEditingFrequencyRuleId(null);
    setIsFrequencyRuleModalOpen(false);
  };

  const openEditLimit = (limit: MockProjectLimit) => {
    setEditing(limit);
    setEditingFrequencyRuleId(null);
    setIsFrequencyRuleModalOpen(false);
  };

  const updateEditing = (patch: Partial<MockProjectLimit>) => {
    setEditing((previous) => previous ? { ...previous, ...patch } : previous);
  };

  const updateEditingReport = (report: ReportType) => {
    const field = getAvailableFieldOptions(report)[0] || getDefaultFieldOption(report);
    setEditing((previous) => previous ? {
      ...previous,
      report,
      field: field.fieldPath,
      unit: field.unit,
      std: field.fallback,
    } : previous);
  };

  const updateEditingField = (fieldPath: string) => {
    if (!editing) return;
    const field = getAvailableFieldOptions(editing.report).find((option) => option.fieldPath === fieldPath);
    setEditing((previous) => previous ? {
      ...previous,
      field: fieldPath,
      unit: field?.unit || previous.unit,
      std: field?.fallback || previous.std,
    } : previous);
  };

  const updateEditingOperator = (operator: LimitOperator) => {
    setEditing((previous) => {
      if (!previous) return previous;
      const currentValue = previous.minValue || previous.maxValue;

      if (operator === 'min') return { ...previous, operator, minValue: currentValue, maxValue: '' };
      if (operator === 'max') return { ...previous, operator, minValue: '', maxValue: currentValue };
      if (operator === 'equals') return { ...previous, operator, minValue: currentValue, maxValue: currentValue };

      return { ...previous, operator };
    });
  };

  const updateEditingProjectValue = (value: string) => {
    setEditing((previous) => {
      if (!previous) return previous;
      const operator = getLimitOperator(previous);

      if (operator === 'max') return { ...previous, minValue: '', maxValue: value };
      if (operator === 'equals') return { ...previous, minValue: value, maxValue: value };

      return { ...previous, minValue: value, maxValue: '' };
    });
  };

  const getProjectLimitPayload = (limit: MockProjectLimit) => ({
    ...(isNumericId(limit.id) ? { id: Number(limit.id) } : {}),
    projectId,
    ...(pipeId ? { pipeId } : {}),
    reportType: limit.report,
    fieldKey: limit.field,
    fieldLabel: getAvailableFieldOptions(limit.report).find((field) => field.fieldPath === limit.field)?.label || limit.field,
    minValue: limit.minValue,
    maxValue: limit.maxValue,
    unit: limit.unit,
    source: limit.source,
    std: limit.std,
    remarks: limit.source,
    detailsJson: {
      operator: getLimitOperator(limit),
      standardFallback: limit.std,
      uiConfig: {
        inputType: 'text',
      },
    },
  });

  const saveEditing = () => {
    if (!editing) return;
    if (projectId) {
      saveProjectLimit(getProjectLimitPayload(editing));
    }
    setLimits((previous) => {
      const exists = previous.some((limit) => limit.id === editing.id);
      if (exists) return previous.map((limit) => limit.id === editing.id ? editing : limit);
      return [editing, ...previous];
    });
    setActiveReport(editing.report);
    setEditing(null);
  };

  const deleteLimit = (limit: MockProjectLimit) => {
    setDeleteTarget({ kind: 'limit', item: limit });
  };

  const confirmDeleteLimit = (limit: MockProjectLimit) => {
    if (isNumericId(limit.id)) {
      deleteProjectLimitApi({ id: Number(limit.id), limitId: Number(limit.id) });
    }
    setLimits((previous) => previous.filter((item) => item.id !== limit.id));
    if (editing?.id === limit.id) setEditing(null);
    setDeleteTarget(null);
  };

  const openAddFrequencyRule = () => {
    const report = activeReport !== 'ALL'
      ? activeReport
      : REPORT_TABS.some((tab) => tab.key === selectedReportType)
        ? selectedReportType as ReportType
        : 'HYDROSTATIC';
    setFrequencyRuleForm(createBlankFrequencyRule(report));
    setEditingFrequencyRuleId(null);
    setEditing(null);
    setIsFieldOptionModalOpen(false);
    setIsFrequencyRuleModalOpen(true);
  };

  const openEditFrequencyRule = (rule: FrequencyRule) => {
    setFrequencyRuleForm(rule);
    setEditingFrequencyRuleId(rule.id);
    setEditing(null);
    setIsFieldOptionModalOpen(false);
    setIsFrequencyRuleModalOpen(true);
  };

  const updateFrequencyRuleForm = (patch: Partial<FrequencyRule>) => {
    setFrequencyRuleForm((previous) => ({ ...previous, ...patch }));
  };

  const saveFrequencyRule = () => {
    const matchingLimit = limits.find((limit) => limit.report === frequencyRuleForm.report && isNumericId(limit.id));
    const ruleToSave: LocalFrequencyRule = {
      ...frequencyRuleForm,
      lotSize: frequencyRuleForm.lotSize.trim() || '-',
      pipeCount: frequencyRuleForm.pipeCount.trim() || '-',
      source: frequencyRuleForm.source.trim() || 'Mock entry',
      reference: frequencyRuleForm.reference.trim() || 'manual setup',
    };
    if (projectId) {
      saveFrequencyRuleApi({
        ...(isNumericId(ruleToSave.id) ? { id: Number(ruleToSave.id) } : {}),
        projectLimitId: ruleToSave.projectLimitId || ruleToSave.project_limit_id || (matchingLimit ? Number(matchingLimit.id) : undefined),
        projectId,
        ...(pipeId ? { pipeId } : {}),
        reportType: ruleToSave.report,
        fieldKey: matchingLimit?.field,
        frequency: ruleToSave.frequency,
        frequencyValue: ruleToSave.pipeCount,
        sampleSize: ruleToSave.lotSize,
        lotSize: ruleToSave.lotSize,
        source: ruleToSave.source,
        reference: ruleToSave.reference,
        detailsJson: {
          source: ruleToSave.source,
          reference: ruleToSave.reference,
          pipeCount: ruleToSave.pipeCount,
        },
      });
    }
    setFrequencyRules((previous) => {
      if (editingFrequencyRuleId) {
        return previous.map((rule) => rule.id === editingFrequencyRuleId ? ruleToSave : rule);
      }
      return [ruleToSave, ...previous];
    });
    setEditingFrequencyRuleId(null);
    setIsFrequencyRuleModalOpen(false);
  };

  const deleteFrequencyRule = (rule: FrequencyRule) => {
    setDeleteTarget({ kind: 'frequency', item: rule as LocalFrequencyRule });
  };

  const confirmDeleteFrequencyRule = (rule: LocalFrequencyRule) => {
    if (isNumericId(rule.id)) {
      deleteFrequencyRuleApi({ id: Number(rule.id), ruleId: Number(rule.id) });
    }
    setFrequencyRules((previous) => previous.filter((item) => item.id !== rule.id));
    if (editingFrequencyRuleId === rule.id) {
      setEditingFrequencyRuleId(null);
      setIsFrequencyRuleModalOpen(false);
    }
    setDeleteTarget(null);
  };

  const confirmDeleteTarget = () => {
    if (!deleteTarget) return;
    if (deleteTarget.kind === 'limit') {
      confirmDeleteLimit(deleteTarget.item);
      return;
    }
    confirmDeleteFrequencyRule(deleteTarget.item);
  };

  const openAddProjectLimitField = () => {
    setFieldOptionForm({
      fieldPath: '',
      symbol: '',
      label: '',
      unit: editing?.unit || '',
      fallback: editing?.std || '',
    });
    setIsFieldOptionModalOpen(true);
  };

  const saveProjectLimitField = () => {
    if (!editing) return;
    const label = fieldOptionForm.label.trim();
    if (!label) return;
    const symbol = fieldOptionForm.symbol?.trim();
    const fieldPath = fieldOptionForm.fieldPath.trim() || createFieldPathFromLabel(editing.report, label, symbol || '');
    const newField: ProjectLimitFieldOption = {
      fieldPath,
      symbol,
      label,
      unit: fieldOptionForm.unit.trim(),
      fallback: fieldOptionForm.fallback.trim() || 'Mock standard fallback',
    };
    setCustomFieldOptionsByType((previous) => ({
      ...previous,
      [editing.report]: [
        ...(previous[editing.report] || []),
        newField,
      ],
    }));
    setEditing((previous) => previous ? {
      ...previous,
      field: newField.fieldPath,
      unit: newField.unit,
      std: newField.fallback,
    } : previous);
    setIsFieldOptionModalOpen(false);
  };

  const reloadProjectLimitData = () => {
    if (!projectId) return;
    const payload = {
      projectId,
      ...(pipeId ? { pipeId } : {}),
    };
    fetchProjectLimits(payload);
    fetchFrequencyRules(payload);
  };

  const resetDefaultProjectLimits = async () => {
    if (!projectId) return;
    setIsResettingDefaults(true);
    try {
      const response = await pipeMillReportApi.resetDefaultProjectLimits({
        projectId,
        ...(pipeId ? { pipeId } : {}),
        limits: PROJECT_LIMITS.map(mapDefaultLimitPayload),
      });
      const resetLimits = Array.isArray(response?.limits) ? response.limits.map(mapApiLimit) : [];
      setLimits(resetLimits);
      setEditing(null);
      reloadProjectLimitData();
    } finally {
      setIsResettingDefaults(false);
    }
  };

  const resetDefaultFrequencyRules = async () => {
    if (!projectId) return;
    setIsResettingDefaults(true);
    try {
      const response = await pipeMillReportApi.resetDefaultFrequencyRules({
        projectId,
        ...(pipeId ? { pipeId } : {}),
        rules: FREQUENCY_RULES.map(mapDefaultFrequencyPayload),
      });
      const resetRules = Array.isArray(response?.frequencyRules) ? response.frequencyRules.map(mapApiFrequencyRule) : [];
      setFrequencyRules(resetRules);
      setEditingFrequencyRuleId(null);
      setIsFrequencyRuleModalOpen(false);
      reloadProjectLimitData();
    } finally {
      setIsResettingDefaults(false);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="!left-0 !top-0 !h-screen !max-h-screen !w-screen !max-w-none !translate-x-0 !translate-y-0 m-0 gap-0 rounded-none border-0 bg-page-bg p-0 shadow-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
        <DialogHeader className="border-b border-[#E2E5EA] bg-white px-6 pb-3 pt-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
              <Button onClick={() => onOpenChange(false)} variant="outline" className="h-9 w-9 shrink-0 rounded-lg border-[#E2E5EA] p-0">
                <ArrowLeft className="h-5 w-5 text-text-grey" />
              </Button>
              <div>
                <DialogTitle className="font-heading text-[20px] font-semibold text-blueprint-blue">
                  Project Limits & ITP
                </DialogTitle>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-heritage-grey">
                  <span className="font-mono text-blueprint-blue">{itpNumber}</span>
                  <span>-</span>
                  <span>{limits.length} overrides</span>
                  <span>-</span>
                  <span>{frequencyRules.length} frequency rules</span>
                  <span>-</span>
                  <span>sour service ON</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={openAddLimit} className="bg-hero-red text-white hover:bg-hero-red-60">
                <Plus className="mr-2 h-4 w-4" />
                Add Limit
              </Button>
              <Button
                type="button"
                onClick={resetDefaultProjectLimits}
                disabled={!projectId || isResettingDefaults}
                variant="outline"
                className="border-[#E2E5EA] bg-white text-blueprint-blue hover:bg-page-bg"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset Default Project Limits
              </Button>
              <Button
                type="button"
                onClick={resetDefaultFrequencyRules}
                disabled={!projectId || isResettingDefaults}
                variant="outline"
                className="border-[#E2E5EA] bg-white text-blueprint-blue hover:bg-page-bg"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset Default Frequency Rules
              </Button>
            </div>
          </div>

          <div className="mt-3 flex gap-2 overflow-x-auto">
            {REPORT_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveReport(tab.key)}
                className={cn(
                  'whitespace-nowrap rounded-lg border px-3 py-2 text-[12px] font-semibold transition-colors',
                  activeReport === tab.key
                    ? 'border-hero-red-20 bg-hero-red-10 text-readable-red'
                    : 'border-[#E2E5EA] bg-white text-blueprint-blue hover:bg-page-bg'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </DialogHeader>

        <div className={cn(
          'grid min-h-0 flex-1 grid-cols-1',
          (editing || isFrequencyRuleModalOpen || isFieldOptionModalOpen) && 'xl:grid-cols-[minmax(0,1fr)_460px] 2xl:grid-cols-[minmax(0,1fr)_520px]'
        )}>
          <div className="min-h-0 overflow-y-auto p-4 sm:p-6">
            <Card className="overflow-hidden border-[#E2E5EA]">
              <CardContent className="p-0">
                <div className="flex items-start justify-between gap-4 border-b border-[#E2E5EA] px-6 py-4">
                  <div>
                    <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Threshold Overrides</h3>
                    <p className="mt-1 text-[12px] text-heritage-grey">
                      Limits resolve as project_limits -&gt; standard_limits -&gt; PENDING_REVIEW
                    </p>
                  </div>
                  <span className="mt-0.5 shrink-0 rounded-full bg-[rgba(0,39,63,0.06)] px-3 py-1 text-[11px] font-semibold text-blueprint-blue">
                    {filteredLimits.length} shown
                  </span>
                </div>

                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">Report</TableHead>
                      <TableHead className="w-[220px]">Field</TableHead>
                      <TableHead className="w-[120px] text-center">Operator</TableHead>
                      <TableHead className="w-[150px] text-right">Min Value</TableHead>
                      <TableHead className="w-[150px] text-right">Max Value</TableHead>
                      <TableHead className="w-[220px] text-right">Standard Fallback</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="w-[120px] text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isProjectLimitsLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center">
                          <div className="inline-flex items-center gap-2 text-[13px] font-semibold text-blueprint-blue">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading project limits...
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : projectLimitsError ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-[13px] text-readable-red">
                          {String(projectLimitsError)}
                        </TableCell>
                      </TableRow>
                    ) : filteredLimits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-[13px] text-heritage-grey">
                          No project limits configured. Use Add Limit or Reset Default Project Limits.
                        </TableCell>
                      </TableRow>
                    ) : filteredLimits.map((limit) => {
                      const isSelected = editing?.id === limit.id;
                      return (
                        <TableRow
                          key={limit.id}
                          onClick={() => openEditLimit(limit)}
                          className={cn('cursor-pointer', isSelected && 'bg-page-bg')}
                        >
                          <TableCell className="align-middle"><span className="font-mono text-[12px]">{limit.report}</span></TableCell>
                          <TableCell className="align-middle">
                            <FieldDisplay report={limit.report} fieldPath={limit.field} customOptions={customFieldOptionsByType[limit.report] || []} />
                          </TableCell>
                          <TableCell className="text-center align-middle font-mono">
                            {getOperatorLabel(getLimitOperator(limit))}
                          </TableCell>
                          <TableCell className="text-right align-middle font-mono">
                            <span className={cn('font-semibold', limit.missing && 'font-normal italic text-heritage-grey')}>
                              {limit.missing ? 'not configured' : limit.minValue || '-'}
                              {!limit.missing && limit.minValue && limit.unit && <span className="ml-1 text-[11px] text-heritage-grey">{limit.unit}</span>}
                            </span>
                          </TableCell>
                          <TableCell className="text-right align-middle font-mono">
                            <span className={cn('font-semibold', limit.missing && 'font-normal italic text-heritage-grey')}>
                              {limit.missing ? 'not configured' : limit.maxValue || '-'}
                              {!limit.missing && limit.maxValue && limit.unit && <span className="ml-1 text-[11px] text-heritage-grey">{limit.unit}</span>}
                            </span>
                          </TableCell>
                          <TableCell className="text-right align-middle font-mono text-heritage-grey">{limit.std}</TableCell>
                          <TableCell className="align-middle"><span className="text-[11px] text-text-grey">{limit.source}</span></TableCell>
                          <TableCell className="text-right align-middle">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                title="Edit"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openEditLimit(limit);
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                title="Delete"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  deleteLimit(limit);
                                }}
                                className="h-8 w-8 p-0 border-readable-red/25 text-readable-red hover:bg-[rgba(196,0,31,0.06)]"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card className="mt-6 overflow-hidden border-[#E2E5EA]">
              <CardContent className="p-0">
                <div className="flex items-center justify-between border-b border-[#E2E5EA] px-6 py-4">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Test Frequency Rules</h3>
                  <Button type="button" variant="outline" size="sm" onClick={openAddFrequencyRule}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Rule
                  </Button>
                </div>
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[160px]">Report Type</TableHead>
                      <TableHead className="w-[150px]">Frequency</TableHead>
                      <TableHead className="w-[130px] text-right">No. Pipes</TableHead>
                      <TableHead className="w-[150px] text-right">Lot Size</TableHead>
                      <TableHead className="w-[180px]">Source</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="w-[120px] text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isFrequencyRulesLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center">
                          <div className="inline-flex items-center gap-2 text-[13px] font-semibold text-blueprint-blue">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading frequency rules...
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : frequencyRulesError ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center text-[13px] text-readable-red">
                          {String(frequencyRulesError)}
                        </TableCell>
                      </TableRow>
                    ) : frequencyRules.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center text-[13px] text-heritage-grey">
                          No frequency rules configured. Use Add Rule or Reset Default Frequency Rules.
                        </TableCell>
                      </TableRow>
                    ) : frequencyRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="align-middle"><span className="font-mono text-[12px]">{rule.report}</span></TableCell>
                        <TableCell className="align-middle"><span className="rounded-full bg-[#EEF0F3] px-2 py-1 text-[10px] font-semibold text-blueprint-blue">{rule.frequency}</span></TableCell>
                        <TableCell className="text-right align-middle font-mono">{rule.pipeCount || '-'}</TableCell>
                        <TableCell className="text-right align-middle font-mono">{rule.lotSize}</TableCell>
                        <TableCell className="align-middle">{rule.source}</TableCell>
                        <TableCell className="align-middle text-text-grey">{rule.reference}</TableCell>
                        <TableCell className="text-right align-middle">
                          <div className="flex justify-end gap-1">
                            <Button type="button" variant="outline" size="sm" title="Edit" onClick={() => openEditFrequencyRule(rule)} className="h-8 w-8 p-0">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button type="button" variant="outline" size="sm" title="Delete" onClick={() => deleteFrequencyRule(rule)} className="h-8 w-8 p-0 border-readable-red/25 text-readable-red hover:bg-[rgba(196,0,31,0.06)]">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

          </div>
          {(editing || isFrequencyRuleModalOpen || isFieldOptionModalOpen) && (
            <aside className="min-h-0 overflow-y-auto border-l border-[#E2E5EA] bg-page-bg p-4">
              {editing && !isFieldOptionModalOpen && (
                <Card className="border-[#E2E5EA]">
                  <CardContent className="p-0">
                    <div className="flex items-start justify-between gap-4 border-b border-[#E2E5EA] bg-white px-4 py-4">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Edit limit</div>
                        <h3 className="mt-1 font-heading text-[16px] font-semibold text-blueprint-blue">{editing.report}</h3>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => setEditing(null)} className="h-8 w-8 p-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="space-y-4 p-4">
                      <Field label="Report">
                        <select
                          value={editing.report}
                          onChange={(event) => updateEditingReport(event.target.value as ReportType)}
                          disabled={!editing.id.startsWith('draft')}
                          className={cn(inputClassName, !editing.id.startsWith('draft') && 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')}
                        >
                          {REPORT_TABS.filter((tab) => tab.key !== 'ALL').map((tab) => (
                            <option key={tab.key} value={tab.key}>{tab.label}</option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Field">
                        <div className="flex gap-2">
                          <select value={editing.field} onChange={(event) => updateEditingField(event.target.value)} className={inputClassName}>
                            {getAvailableFieldOptions(editing.report).map((field) => (
                              <option key={field.fieldPath} value={field.fieldPath}>{formatProjectFieldOption(field)}</option>
                            ))}
                          </select>
                          <Button type="button" variant="outline" onClick={openAddProjectLimitField} className="shrink-0">
                            <Plus className="mr-2 h-4 w-4" />
                            Field
                          </Button>
                        </div>
                      </Field>

                      <Field label="Operator">
                        <div className="flex flex-wrap gap-2">
                          {LIMIT_OPERATOR_OPTIONS.map((option) => {
                            const active = getLimitOperator(editing) === option.value;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => updateEditingOperator(option.value)}
                                className={cn(
                                  'rounded-lg border px-3 py-2 text-[12px] font-semibold transition-colors',
                                  active ? 'border-hero-red-20 bg-hero-red-10 text-readable-red' : 'border-[#E2E5EA] bg-white text-blueprint-blue hover:bg-page-bg'
                                )}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </Field>

                      {getLimitOperator(editing) === 'range' ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Field label="Min Value">
                            <input value={editing.minValue} onChange={(event) => updateEditing({ minValue: event.target.value })} className={inputClassName} />
                          </Field>
                          <Field label="Max Value">
                            <input value={editing.maxValue} onChange={(event) => updateEditing({ maxValue: event.target.value })} className={inputClassName} />
                          </Field>
                        </div>
                      ) : (
                        <Field label="Project Value">
                          <div className="flex items-center gap-2">
                            <input
                              value={getLimitOperator(editing) === 'max' ? editing.maxValue : editing.minValue}
                              onChange={(event) => updateEditingProjectValue(event.target.value)}
                              className={inputClassName}
                            />
                            {editing.unit && <span className="min-w-8 text-[12px] text-heritage-grey">{editing.unit}</span>}
                          </div>
                        </Field>
                      )}

                      <Field label="Unit">
                        <select value={editing.unit} onChange={(event) => updateEditing({ unit: event.target.value })} className={inputClassName}>
                          {getUnitOptions(editing.report, editing.unit).map((unit) => (
                            <option key={unit || 'none'} value={unit}>{unit || 'No unit'}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Standard fallback">
                        <input value={editing.std} onChange={(event) => updateEditing({ std: event.target.value })} className={inputClassName} />
                      </Field>
                      <Field label="Source citation">
                        <input value={editing.source} onChange={(event) => updateEditing({ source: event.target.value })} className={inputClassName} />
                      </Field>

                      <div className="rounded-xl border border-[#B8D8F0] bg-[rgba(0,39,63,0.04)] p-4">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-blueprint-blue">Impact Preview</div>
                        <p className="mt-2 text-[13px] text-text-grey">Saving this limit marks affected reports for re-evaluation when backend integration is enabled.</p>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 border-t border-[#E2E5EA] bg-white px-4 py-4">
                      <Button type="button" variant="outline" onClick={saveEditing}>Save Draft</Button>
                      <Button type="button" onClick={saveEditing} className="bg-hero-red text-white hover:bg-hero-red-60">Save & Re-evaluate</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {editing && isFieldOptionModalOpen && (
                <Card className="border-[#E2E5EA]">
                  <CardContent className="p-0">
                    <div className="flex items-start justify-between gap-4 border-b border-[#E2E5EA] bg-white px-4 py-4">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Add field</div>
                        <h3 className="mt-1 font-heading text-[16px] font-semibold text-blueprint-blue">{editing.report}</h3>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => setIsFieldOptionModalOpen(false)} className="h-8 w-8 p-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-4 p-4">
                      <Field label="Symbol">
                        <input value={fieldOptionForm.symbol || ''} onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, symbol: event.target.value }))} placeholder="Example: P" className={inputClassName} />
                      </Field>
                      <Field label="Field Name">
                        <input value={fieldOptionForm.label} onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, label: event.target.value }))} placeholder="Example: Phosphorus" className={inputClassName} />
                      </Field>
                      <Field label="Field Path">
                        <input value={fieldOptionForm.fieldPath} onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, fieldPath: event.target.value }))} placeholder="Auto: elements.P" className={inputClassName} />
                      </Field>
                      <Field label="Unit">
                        <select value={fieldOptionForm.unit} onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, unit: event.target.value }))} className={inputClassName}>
                          {getUnitOptions(editing.report, fieldOptionForm.unit).map((unit) => (
                            <option key={unit || 'none'} value={unit}>{unit || 'No unit'}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Standard fallback">
                        <input value={fieldOptionForm.fallback} onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, fallback: event.target.value }))} placeholder="Example: <= 0.025 (PSL2)" className={inputClassName} />
                      </Field>
                    </div>
                    <div className="flex justify-end gap-2 border-t border-[#E2E5EA] bg-white px-4 py-4">
                      <Button type="button" onClick={saveProjectLimitField} className="bg-hero-red text-white hover:bg-hero-red-60">Add Field</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {isFrequencyRuleModalOpen && !editing && (
                <Card className="border-[#E2E5EA]">
                  <CardContent className="p-0">
                    <div className="flex items-start justify-between gap-4 border-b border-[#E2E5EA] bg-white px-4 py-4">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency rule</div>
                        <h3 className="mt-1 font-heading text-[16px] font-semibold text-blueprint-blue">{editingFrequencyRuleId ? 'Edit rule' : 'Add rule'}</h3>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => setIsFrequencyRuleModalOpen(false)} className="h-8 w-8 p-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-4 p-4">
                      <Field label="Report Type">
                        <select value={frequencyRuleForm.report} onChange={(event) => updateFrequencyRuleForm({ report: event.target.value as ReportType })} disabled={Boolean(editingFrequencyRuleId)} className={cn(inputClassName, editingFrequencyRuleId && 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')}>
                          {REPORT_TABS.filter((tab) => tab.key !== 'ALL').map((tab) => (
                            <option key={tab.key} value={tab.key}>{tab.label}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Frequency">
                        <select value={frequencyRuleForm.frequency} onChange={(event) => updateFrequencyRuleForm({ frequency: event.target.value })} className={inputClassName}>
                          {FREQUENCY_OPTIONS.map((frequency) => (
                            <option key={frequency} value={frequency}>{frequency}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Number of Pipes">
                        <input
                          value={frequencyRuleForm.pipeCount}
                          onChange={(event) => updateFrequencyRuleForm({ pipeCount: event.target.value })}
                          placeholder="Example: 50 or 1"
                          className={inputClassName}
                        />
                      </Field>
                      <Field label="Lot Size">
                        <input value={frequencyRuleForm.lotSize} onChange={(event) => updateFrequencyRuleForm({ lotSize: event.target.value })} placeholder="Example: 50 pipes, one heat, or -" className={inputClassName} />
                      </Field>
                      <Field label="Source">
                        <input value={frequencyRuleForm.source} onChange={(event) => updateFrequencyRuleForm({ source: event.target.value })} placeholder="Example: ITP, ITP override, API 5L" className={inputClassName} />
                      </Field>
                      <Field label="Reference">
                        <input value={frequencyRuleForm.reference} onChange={(event) => updateFrequencyRuleForm({ reference: event.target.value })} placeholder="Example: ITP 4.2" className={inputClassName} />
                      </Field>
                    </div>
                    <div className="flex justify-end gap-2 border-t border-[#E2E5EA] bg-white px-4 py-4">
                      <Button type="button" onClick={saveFrequencyRule} className="bg-hero-red text-white hover:bg-hero-red-60">{editingFrequencyRuleId ? 'Save Changes' : 'Add Rule'}</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </aside>
          )}
        </div>
        </div>
      </DialogContent>
    </Dialog>

    <DeleteConfirmationDialog
      isOpen={Boolean(deleteTarget)}
      onClose={() => setDeleteTarget(null)}
      onConfirm={confirmDeleteTarget}
      itemToDelete={deleteTarget?.item || null}
      itemType={deleteTarget?.kind === 'frequency' ? 'Frequency Rule' : 'Project Limit'}
      itemName={
        deleteTarget?.kind === 'frequency'
          ? `${deleteTarget.item.report} - ${deleteTarget.item.frequency}`
          : deleteTarget
            ? `${deleteTarget.item.report} - ${deleteTarget.item.field}`
            : ''
      }
      title={deleteTarget?.kind === 'frequency' ? 'Delete Frequency Rule' : 'Delete Project Limit'}
      warningMessage={
        deleteTarget?.kind === 'frequency'
          ? 'This frequency rule will be removed and saved to the database.'
          : 'This project limit will be removed and saved to the database.'
      }
    />
    </>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block space-y-1">
    <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-heritage-grey">{label}</span>
    {children}
  </label>
);

const FieldDisplay: React.FC<{ report: ReportType; fieldPath: string; customOptions: ProjectLimitFieldOption[] }> = ({
  report,
  fieldPath,
  customOptions,
}) => {
  const field = [...(PROJECT_LIMIT_FIELDS[report] || []), ...customOptions].find((option) => option.fieldPath === fieldPath);

  if (!field) {
    return <span className="font-mono text-[12px] font-semibold">{fieldPath}</span>;
  }

  return (
    <div>
      <div className="font-semibold text-blueprint-blue">
        {field.symbol ? `${field.symbol} - ${field.label}` : field.label}
      </div>
      <div className="mt-1 font-mono text-[10px] text-heritage-grey">{field.fieldPath}</div>
    </div>
  );
};

